const chat_page_limit = 20;
const message_page_limit = 50;
const backend_source = "backend";
const local_source = "local";

export default {
  create_message(sender, text, overrides = {}) {
    return ({
      id: overrides.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      sender,
      text,
      created_at: overrides.created_at || new Date().toISOString(),
    });
  },

  create_typing_message() {
    return ({
      ...this.create_message("assistant", ""),
      is_typing: true,
      thinking_steps: this.create_thinking_steps(),
    });
  },

  create_thinking_steps(query_data = null) {
    const query = this.get_thinking_query(query_data);
    const logs = this.get_thinking_logs(query);

    if (!logs.length) {
      return [this.create_initial_thinking_step(query)];
    }

    return logs.map((log, index) => this.create_thinking_step(log, index, query));
  },

  get_thinking_query(query_data) {
    if (!query_data) return ({});
    return query_data.query || query_data;
  },

  get_thinking_logs(query) {
    return Array.isArray(query.logs) ? query.logs : [];
  },

  create_initial_thinking_step(query) {
    const detail = query.id
      ? `Job ${query.id} is waiting for worker updates.`
      : "Submitting query job.";

    return ({
      label: this.format_query_status(query.status || "QUEUED"),
      detail,
      status: "active",
    });
  },

  create_thinking_step(log, index, query) {
    return ({
      label: this.format_stage_label(log.stage),
      detail: this.format_stage_detail(log, query),
      status: this.get_thinking_step_status(log, index, query),
    });
  },

  get_thinking_step_status(log, index, query) {
    if (this.did_thinking_step_fail(log, query)) return "failed";
    const terminal = this.is_terminal_query(query);
    const is_last_log = index === query.logs.length - 1;
    if (!terminal && is_last_log) return "active";
    return "complete";
  },

  did_thinking_step_fail(log, query) {
    return log.status === "FAILED" || query.status === "FAILED";
  },

  is_terminal_query(query) {
    return ["COMPLETED", "FAILED"].includes(query.status);
  },

  create_conversation(overrides = {}) {
    const now = new Date().toISOString();
    const messages = overrides.messages || [
      this.create_message(
        "assistant",
        "Hello! I'm BCX Assist. How can I help you today?"
      ),
    ];

    return ({
      id: overrides.id || `conversation-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      title: overrides.title || "New conversation",
      preview: overrides.preview || this.get_messages_preview(messages),
      created_at: overrides.created_at || now,
      updated_at: overrides.updated_at || now,
      source: overrides.source || local_source,
      message_count: overrides.message_count || messages.length,
      messages,
      messages_loaded: overrides.messages_loaded ?? overrides.source !== backend_source,
      messages_next_cursor: overrides.messages_next_cursor || null,
      is_loading_messages: false,
    });
  },

  create_backend_conversation(summary) {
    return this.create_conversation({
      id: String(summary.chat_id || "default"),
      title: summary.title || "New conversation",
      preview: summary.preview || "",
      created_at: summary.created_at,
      updated_at: summary.updated_at || summary.created_at,
      source: backend_source,
      message_count: summary.message_count || 0,
      messages: [],
      messages_loaded: false,
      messages_next_cursor: null,
    });
  },

  async load_conversations(preferred_active_id = null) {
    this.is_loading_chat_history = true;
    this.chat_history_next_cursor = null;

    try {
      const page = await this.fetch_backend_conversation_page();
      this.chat_history_next_cursor = page.next_cursor;
      this.has_loaded_backend_history = true;
      this.chat_history_load_error = "";
      this.conversations = page.conversations.length
        ? this.merge_backend_conversation_page(page.conversations)
        : [this.create_conversation()];
    } catch (error) {
      this.has_loaded_backend_history = false;
      this.chat_history_load_error = error?.message || "BCX Assist history is unavailable.";
      this.log_chat_history_error(error);
      this.conversations = [this.create_conversation()];
    } finally {
      this.is_loading_chat_history = false;
    }

    this.restore_active_conversation(preferred_active_id);
    await this.ensure_active_conversation_messages();

    if (this.should_start_new_after_history_load && this.is_open) {
      this.start_fresh_open_conversation();
    }
  },

  async load_more_conversations() {
    if (!this.chat_history_next_cursor || this.is_loading_chat_history) return false;
    this.is_loading_chat_history = true;

    try {
      const page = await this.fetch_backend_conversation_page({
        cursor: this.chat_history_next_cursor,
      });
      this.chat_history_next_cursor = page.next_cursor;
      this.conversations = this.merge_backend_conversation_page(page.conversations, {
        append: true,
      });
      return true;
    } catch {
      return false;
    } finally {
      this.is_loading_chat_history = false;
    }
  },

  async refresh_conversations_from_backend(preferred_active_id = null) {
    const page = await this.fetch_backend_conversation_page();
    this.chat_history_next_cursor = page.next_cursor;
    if (!page.conversations.length) return false;

    this.conversations = this.merge_backend_conversation_page(page.conversations);
    this.restore_active_conversation(preferred_active_id || this.active_conversation_id);
    await this.load_conversation_messages(this.active_conversation_id, { replace: true });
    return true;
  },

  async refresh_conversations_from_backend_safely(preferred_active_id = null) {
    try {
      return await this.refresh_conversations_from_backend(preferred_active_id);
    } catch {
      return false;
    }
  },

  retry_load_conversations_if_needed() {
    if (this.has_loaded_backend_history || this.is_loading_chat_history) return false;
    return this.load_conversations(this.active_conversation_id);
  },

  log_chat_history_error(error) {
    if (typeof console === "undefined" || !console.warn) return;
    console.warn("BCX Assist history could not be loaded.", error);
  },

  async fetch_backend_conversation_page(params = {}) {
    let page;
    try {
      page = await this.fetch_assist_chat_summaries({
        limit: chat_page_limit,
        ...params,
      });
    } catch (error) {
      if (!this.should_use_history_summary_fallback(error)) throw error;
      return this.fetch_backend_conversation_page_from_history(params);
    }
    return ({
      conversations: page.results.map((summary) => this.create_backend_conversation(summary)),
      next_cursor: page.next_cursor || null,
    });
  },

  should_use_history_summary_fallback(error) {
    return [404, 405].includes(Number(error?.status));
  },

  async fetch_backend_conversation_page_from_history(params = {}) {
    const page = await this.fetch_assist_history({
      limit: chat_page_limit,
      ...params,
    });
    return ({
      conversations: this.history_records_to_conversation_summaries(page.results)
        .map((summary) => this.create_backend_conversation(summary)),
      next_cursor: page.next_cursor || null,
    });
  },

  history_records_to_conversation_summaries(records) {
    const summaries_by_chat = new Map();
    (records || []).forEach((record) => {
      const chat_id = String(record.chat_id || "default");
      const existing = summaries_by_chat.get(chat_id);
      const record_updated_at = record.completed_at || record.created_at;
      const record_updated_at_value = this.get_timestamp_sort_value(record_updated_at);

      if (!existing) {
        summaries_by_chat.set(chat_id, this.create_conversation_summary_from_record(record));
        return;
      }

      existing.message_count += 1;
      if (record_updated_at_value > existing.updated_at_value) {
        existing.updated_at = record_updated_at;
        existing.updated_at_value = record_updated_at_value;
        existing.preview = this.get_history_record_assistant_text(record)
          || record.query_text
          || existing.preview;
        existing.status = record.status || existing.status;
        existing.latest_query_id = record.id || existing.latest_query_id;
      }

      const record_created_at_value = this.get_timestamp_sort_value(record.created_at);
      if (record_created_at_value < existing.created_at_value) {
        existing.created_at = record.created_at;
        existing.created_at_value = record_created_at_value;
        existing.title = record.query_text || existing.title;
      }
    });

    return [...summaries_by_chat.values()].map((summary) => {
      const { created_at_value, updated_at_value, ...public_summary } = summary;
      return public_summary;
    });
  },

  create_conversation_summary_from_record(record) {
    const updated_at = record.completed_at || record.created_at;
    return ({
      chat_id: String(record.chat_id || "default"),
      title: record.query_text || "New conversation",
      preview: this.get_history_record_assistant_text(record) || record.query_text || "",
      status: record.status || "",
      message_count: 1,
      created_at: record.created_at,
      updated_at,
      latest_query_id: record.id || null,
      created_at_value: this.get_timestamp_sort_value(record.created_at),
      updated_at_value: this.get_timestamp_sort_value(updated_at),
    });
  },

  async fetch_backend_message_page(conversation_id, params = {}) {
    return this.fetch_assist_history({
      chat_id: conversation_id,
      limit: message_page_limit,
      ...params,
    });
  },

  merge_backend_conversation_page(backend_conversations, options = {}) {
    const incoming_ids = new Set(backend_conversations.map((conversation) => conversation.id));
    const existing_by_id = new Map(
      this.conversations.map((conversation) => [conversation.id, conversation])
    );
    const merged_incoming = backend_conversations.map((conversation) => (
      this.merge_existing_conversation_state(existing_by_id.get(conversation.id), conversation)
    ));

    if (options.append) {
      const existing_conversations = this.conversations.filter(
        (conversation) => !incoming_ids.has(conversation.id)
      );
      return [...existing_conversations, ...merged_incoming];
    }

    const local_drafts = this.conversations.filter((conversation) => (
      conversation.source !== backend_source && !incoming_ids.has(conversation.id)
    ));
    return [...local_drafts, ...merged_incoming];
  },

  merge_existing_conversation_state(existing_conversation, incoming_conversation) {
    if (!existing_conversation) return incoming_conversation;
    const merged_conversation = this.merge_conversation_summary(
      existing_conversation,
      incoming_conversation
    );
    return ({
      ...merged_conversation,
      messages: existing_conversation.messages?.length
        ? existing_conversation.messages
        : merged_conversation.messages,
      messages_loaded: existing_conversation.messages_loaded
        || merged_conversation.messages_loaded,
      messages_next_cursor: existing_conversation.messages_next_cursor
        || merged_conversation.messages_next_cursor,
      is_loading_messages: false,
    });
  },

  merge_conversation_summary(existing_conversation, incoming_conversation) {
    const existing_updated_at = this.get_timestamp_sort_value(existing_conversation.updated_at);
    const incoming_updated_at = this.get_timestamp_sort_value(incoming_conversation.updated_at);
    const latest_summary = existing_updated_at > incoming_updated_at
      ? existing_conversation
      : incoming_conversation;
    const existing_created_at = this.get_timestamp_sort_value(existing_conversation.created_at);
    const incoming_created_at = this.get_timestamp_sort_value(incoming_conversation.created_at);
    const earliest_summary = existing_created_at <= incoming_created_at
      ? existing_conversation
      : incoming_conversation;

    return ({
      ...incoming_conversation,
      title: earliest_summary.title || incoming_conversation.title,
      preview: latest_summary.preview || incoming_conversation.preview,
      created_at: earliest_summary.created_at || incoming_conversation.created_at,
      updated_at: latest_summary.updated_at || incoming_conversation.updated_at,
      message_count: Math.max(
        existing_conversation.message_count || 0,
        incoming_conversation.message_count || 0
      ),
    });
  },

  async ensure_active_conversation_messages(conversation_id = this.active_conversation_id) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation || conversation.source !== backend_source) return false;
    if (conversation.messages_loaded || conversation.is_loading_messages) return false;
    try {
      return await this.load_conversation_messages(conversation_id, { replace: true });
    } catch {
      return false;
    }
  },

  async load_conversation_messages(conversation_id, options = {}) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation || conversation.is_loading_messages) return false;

    conversation.is_loading_messages = true;
    try {
      const page = await this.fetch_backend_message_page(conversation_id, {
        cursor: options.cursor || null,
      });
      const page_messages = this.history_records_to_messages(page.results);
      conversation.messages = options.prepend
        ? this.merge_message_pages(page_messages, conversation.messages)
        : page_messages;
      conversation.messages_loaded = true;
      conversation.messages_next_cursor = page.next_cursor || null;
      conversation.message_count = Math.max(
        conversation.message_count || 0,
        Math.ceil(conversation.messages.length / 2)
      );
      conversation.preview = this.get_messages_preview(conversation.messages)
        || conversation.preview;
      if (!options.prepend) this.scroll_to_latest_message({ settle: true });
      return true;
    } finally {
      conversation.is_loading_messages = false;
    }
  },

  async load_older_active_messages() {
    const conversation = this.active_conversation;
    if (!conversation?.messages_next_cursor || conversation.is_loading_messages) return false;
    const scroll_state = this.$refs.chat_messages?.capture_scroll_state?.();
    try {
      const loaded = await this.load_conversation_messages(conversation.id, {
        cursor: conversation.messages_next_cursor,
        prepend: true,
      });
      if (loaded && scroll_state) {
        this.$refs.chat_messages?.restore_scroll_state?.(scroll_state);
      }
      return loaded;
    } catch {
      return false;
    }
  },

  merge_message_pages(incoming_messages, existing_messages) {
    const existing_ids = new Set(existing_messages.map((message) => message.id));
    return [
      ...incoming_messages.filter((message) => !existing_ids.has(message.id)),
      ...existing_messages,
    ];
  },

  history_records_to_messages(records) {
    if (!Array.isArray(records)) return [];
    return [...records]
      .sort((left, right) => (
        this.get_timestamp_sort_value(left.created_at)
        - this.get_timestamp_sort_value(right.created_at)
      ))
      .flatMap((record) => this.history_record_messages(record));
  },

  history_record_messages(record) {
    const record_id = this.get_history_record_id(record);
    const messages = [
      this.create_message("user", record.query_text || "", {
        id: `${record_id}-user`,
        created_at: record.created_at,
      }),
    ];
    const assistant_text = this.get_history_record_assistant_text(record);
    if (assistant_text) {
      messages.push(
        this.create_message("assistant", assistant_text, {
          id: `${record_id}-assistant`,
          created_at: record.completed_at || record.created_at,
        })
      );
    }
    return messages;
  },

  get_history_record_assistant_text(record) {
    if (record.response_text) return record.response_text;
    const status = String(record.status || "").toUpperCase();
    if (status === "FAILED") {
      return record.error_message || "BCX Assist could not process that request.";
    }
    if (["QUEUED", "PROCESSING"].includes(status)) {
      return "BCX Assist is still working on this request.";
    }
    return "";
  },

  get_history_record_id(record) {
    const stable_id = record.id || `${record.chat_id || "default"}-${record.created_at}`;
    return `history-${stable_id}`;
  },

  get_timestamp_sort_value(value) {
    const timestamp = Date.parse(value || "");
    return Number.isNaN(timestamp) ? 0 : timestamp;
  },

  get_messages_preview(messages) {
    const last_message = [...(messages || [])].reverse().find((message) => (
      String(message?.text || "").trim()
    ));
    return last_message?.text || "";
  },

  restore_active_conversation(preferred_active_id = null) {
    const saved_active_id = preferred_active_id || this.read_active_conversation_id();
    const has_saved_active = this.conversations.some(
      (item) => item.id === saved_active_id
    );
    this.active_conversation_id = has_saved_active
      ? saved_active_id
      : this.conversations[0]?.id || null;
  },

  read_active_conversation_id() {
    try {
      return localStorage.getItem(this.active_storage_key);
    } catch {
      return null;
    }
  },

  save_active_conversation(id) {
    if (!id) return false;

    try {
      localStorage.setItem(this.active_storage_key, id);
      return true;
    } catch {
      // Local storage can be unavailable or full; chat should keep working.
      return false;
    }
  },

  start_new_conversation(options = {}) {
    if (options.reuse_empty_active && this.is_empty_local_conversation(this.active_conversation)) {
      this.user_message = "";
      return;
    }

    const conversation = this.create_conversation();
    this.conversations.unshift(conversation);
    this.active_conversation_id = conversation.id;
    this.user_message = "";
  },

  is_empty_local_conversation(conversation) {
    return this.is_local_conversation(conversation)
      && this.has_no_user_messages(conversation)
      && this.has_only_welcome_message(conversation);
  },

  is_local_conversation(conversation) {
    return Boolean(conversation) && conversation.source !== backend_source;
  },

  has_no_user_messages(conversation) {
    const messages = conversation.messages || [];

    return !messages.some((message) => message.sender === "user");
  },

  has_only_welcome_message(conversation) {
    const messages = conversation.messages || [];
    const assistant_messages = messages.filter((message) => message.sender === "assistant");

    return assistant_messages.length === 1
      && assistant_messages[0].text === "Hello! I'm BCX Assist. How can I help you today?";
  },

  select_conversation(id) {
    this.active_conversation_id = id;
    this.ensure_active_conversation_messages(id);
  },

  get_conversation_by_id(conversation_id) {
    return this.conversations.find((item) => item.id === conversation_id) || null;
  },

  delete_conversation(id) {
    if (this.conversations.length <= 1) return;

    const conversation = this.get_conversation_by_id(id);
    if (conversation?.source === backend_source) return;

    const index = this.conversations.findIndex((item) => item.id === id);
    this.conversations = this.conversations.filter((item) => item.id !== id);
    if (this.active_conversation_id !== id) return;

    const next_index = Math.max(0, Math.min(index, this.conversations.length - 1));
    this.active_conversation_id = this.conversations[parseInt(next_index, 10)].id;
  },

  touch_active_conversation() {
    if (!this.active_conversation) return;
    this.active_conversation.updated_at = new Date().toISOString();
    this.active_conversation.preview = this.get_messages_preview(this.active_conversation.messages);
  },

  touch_conversation(conversation_id) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation) return;
    conversation.updated_at = new Date().toISOString();
    conversation.preview = this.get_messages_preview(conversation.messages) || conversation.preview;
  },

  get_conversation_title(conversation) {
    const first_user_message = conversation.messages?.find(
      (message) => message.sender === "user"
    );
    return first_user_message?.text || conversation.title || "New conversation";
  },
};
